<template>
    <div class="flex flex-col gap-y-0.5">
        <template
            v-for="entry in menu"
            :key="entry.id"
        >
            <div
                v-if="entry.divider"
                class="my-2 border-t border-gray-100 dark:border-gray-730"
                :class="mini ? 'mx-2' : 'mx-4'"
            ></div>

            <RoutingSideBarSection
                v-else-if="entry.section"
                :section="entry"
                :mini="mini"
                :isShowPopup="isShowPopup"
                :popupPosition="popupPosition"
                @toggleSection="onToggleSection"
                @openSubMenu="onOpenSubMenu"
                @mouseenter="onMouseenter"
                @mouseleave="onMouseleave"
                @popupMouseenter="mouseenter"
            />

            <RoutingSideBarItem
                v-else
                :route="entry"
                :mini="mini"
                :isShowPopup="isShowPopup"
                :popupPosition="popupPosition"
                @openSubMenu="onOpenSubMenu"
                @mouseenter="onMouseenter"
                @mouseleave="onMouseleave"
                @popupMouseenter="mouseenter"
            />
        </template>
    </div>
</template>

<script setup>
import {
    computed, ref, watch, inject, onMounted,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'

import RoutingSideBarSection from '@/components/navigation/RoutingSideBar/RoutingSideBarSection.vue'
import RoutingSideBarItem from '@/components/navigation/RoutingSideBar/RoutingSideBarItem.vue'

import { useRouterStore } from '@/store/routerStore'
import { useAuthorizationStore } from '@/store/authorizationStore'
import { usePermissionsStore } from '@/store/permissionsStore'

import { useRoutingMenu } from '@/composables/useRoutingMenu'

import { CALL_MENU_ROUTES_BUYER, CALL_MENU_ROUTES_MANAGER, CALL_MENU_ROUTES_VENDOR } from '@/constants/callMenu'
import { APP_ROLES } from '@/constants/appRoles'
import { NAVIGATION_ACTIONS } from '@/constants/navigationActions'
import { viewPorts } from '@/constants/viewPorts'

const props = defineProps({
    mini: {
        type: Boolean,
        default: false,
    },
    showMobileSideBars: {
        type: Boolean,
        default: false,
    },
})

const routerStore = useRouterStore()
const authorizationStore = useAuthorizationStore()
const permissionsStore = usePermissionsStore()

const { checkCurrentLink } = useRoutingMenu()

const router = useRouter()
const route = useRoute()

const currentViewSize = inject('currentViewSize')

const menu = ref([])

const popupPosition = ref({})
const isShowPopup = ref(-1)

const timerId = ref(null)

const isMbView = computed(() => viewPorts.mb >= currentViewSize.value)

const CALL_MENU_BY_ROLE = {
    [APP_ROLES.vendor]: CALL_MENU_ROUTES_VENDOR,
    [APP_ROLES.buyer]: CALL_MENU_ROUTES_BUYER,
}

const getCallMenuByRole = computed(() => CALL_MENU_BY_ROLE[authorizationStore.role] || CALL_MENU_ROUTES_MANAGER)

onMounted(() => {
    menu.value = currentMenu()
    openActiveSections()
})

function handleRouteMini({ routeName, paramsNames }) {
    if (!props.mini) return

    isShowPopup.value = -1
    router.push({ name: routeName, params: { ...routerStore.getParams(paramsNames) } })
}

function onOpenSubMenu(route) {
    menu.value = mapSubMenuState(menu.value, route)

    handleRouteMini(route)
    navigationActionHandler(route)
}

function mapSubMenuState(items, activeRoute) {
    const containsActive = items.some((item) => item.name === activeRoute.name)

    return items.map((item) => {
        if (item.section) {
            return { ...item, items: mapSubMenuState(item.items, activeRoute) }
        }

        if (item.name === activeRoute.name) {
            return item.subMenu ? { ...item, isOpenSub: !item.isOpenSub } : item
        }

        if (containsActive && item.isOpenSub) {
            return { ...item, isOpenSub: false }
        }

        return item
    })
}

function onToggleSection(section) {
    section.isOpenSection = !section.isOpenSection
}

function navigationActionHandler(route) {
    if (!route.hasOwnProperty('action')) {
        return
    }

    switch (route.action) {
    case NAVIGATION_ACTIONS.logout:
        logout()
    }
}

function logout() {
    authorizationStore.logout()
}

function onMouseenter(route, event) {
    const { subMenu } = route
    if (!props.mini || !subMenu || isMbView.value) {
        return
    }
    openPopupSubMenu(route, event)
}

function openPopupSubMenu({ id }, { target }) {
    clearTimeout(timerId.value)
    isShowPopup.value = id

    const { left, top, width } = target.getBoundingClientRect()

    popupPosition.value = {
        position: 'fixed',
        left: `${left - (width / 2) + 66}px`,
        top: `${top}px`,
    }
}

function mouseenter(id) {
    clearTimeout(timerId.value)
    isShowPopup.value = id
}

function onMouseleave() {
    timerId.value = setTimeout(() => {
        isShowPopup.value = -1
    }, 100)
}

function currentMenu() {
    return normalizeDividers(permissionsStore.filterMenuByPermission(getCallMenuByRole.value))
}

function normalizeDividers(entries) {
    const result = []

    entries.forEach((entry) => {
        if (entry.divider) {
            const prev = result[result.length - 1]
            if (!prev || prev.divider) return
        }

        result.push(entry)
    })

    return result
}

function openActiveSections() {
    menu.value.forEach((entry) => {
        if (!entry.section || !entry.isFirstLoadSection) {
            return
        }

        const hasActive = entry.items.some((item) => checkCurrentLink(item, item.subMenu))

        if (hasActive) {
            entry.isOpenSection = true
        }

        entry.isFirstLoadSection = false
    })
}

function resetActiveMenu() {
    menu.value.forEach((entry) => {
        if (entry.section) {
            entry.items.forEach((item) => {
                if (item.isOpenSub) {
                    item.isOpenSub = false
                }
            })
            return
        }

        if (entry.isOpenSub) {
            entry.isOpenSub = false
        }
    })
}

watch(route, () => {
    popupPosition.value = {}
})

watch(() => authorizationStore.workspace, () => {
    resetActiveMenu()
    menu.value = currentMenu()
    openActiveSections()
})
</script>
